<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, contact ? 'device-active--heat' : '']"
      :style="{ '--pin-active-color': HEAT_ACCENT }"
      :title="device.deviceName">
      <ControlStatusRing :value="highestDemand" :accent-color="HEAT_ACCENT">
        <ControlIcon
          :name="device.icon || 'local_fire_department'"
          :size="pinIconSize"
          :style="contact ? { color: HEAT_ACCENT } : {}" />
      </ControlStatusRing>
      <div v-if="contact" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- ── Grid mode ─────────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'local_fire_department'"
      :name="displayName"
      :is-on="contact"
      active-color="orange-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- The controller's own sheet: the contact it drives, the furnace switch,
       and one row per zone — its real name (from the thermostat the pick
       points at), its reading, and the band the handler acts on. A generic
       switch card could show none of that. -->
  <template v-else>
    <div class="heat-sheet">
      <div class="heat-hero">
        <button
          type="button"
          class="heat-circle"
          :class="{ 'heat-circle--on': contact, 'heat-circle--blocked': !state.enabled }"
          :disabled="controlling"
          :title="
            state.enabled
              ? tt('Block heat demand', 'Prepreči potrebo po toploti')
              : tt('Allow heat demand', 'Dovoli potrebo po toploti')
          "
          @click="toggleEnabled">
          <ControlIcon :name="device.icon || 'local_fire_department'" size="2.4rem" />
        </button>
        <div class="heat-state" :class="{ 'heat-state--on': contact, 'heat-state--blocked': !state.enabled }">
          {{ stateWord }}
        </div>
        <div class="heat-caption">{{ caption }}</div>
      </div>

      <p class="heat-band">
        {{
          tt(
            `Raises at ${formatPercent(thresholds.threshold)} · releases below ${formatPercent(thresholds.releaseAt)}`,
            `Vklop nad ${formatPercent(thresholds.threshold)} · izklop pod ${formatPercent(thresholds.releaseAt)}`,
          )
        }}
      </p>

      <div v-if="zones.length === 0" class="heat-empty">
        {{ tt('No zone demand available on this controller.', 'Na tem krmilniku ni na voljo podatkov o conah.') }}
      </div>

      <ul v-else class="heat-zones">
        <li
          v-for="zone in zones"
          :key="zone.demandKey"
          class="heat-zone"
          :class="{
            'heat-zone--asking': zone.asking,
            'heat-zone--held': !zone.asking && isHeld(zone),
            'heat-zone--silent': zone.demand === null,
            'heat-zone--window': zone.windowOpen,
          }">
          <div class="heat-zone__head">
            <span class="heat-zone__name" :class="{ 'heat-zone__name--placeholder': !zone.named }">
              {{ zone.name }}
            </span>
            <span class="heat-zone__value">
              {{ zone.demand === null ? '—' : formatPercent(zone.demand) }}
            </span>
          </div>
          <div class="heat-rail" role="img" :aria-label="`${zone.name}: ${zone.demand ?? 0} %`">
            <span class="heat-rail__band" :style="bandStyle" />
            <span
              class="heat-rail__fill"
              :style="{ width: `${zone.demand === null ? 0 : clampPercent(zone.demand)}%` }" />
            <span class="heat-rail__mark" :style="markStyle" />
          </div>
          <div class="heat-zone__meta">
            <span class="heat-zone__word">{{ zoneWord(zone) }}</span>
            <span v-if="zone.detail" class="heat-zone__detail">{{ zone.detail }}</span>
          </div>
        </li>
      </ul>

      <p v-if="noSourceNote" class="heat-note heat-note--warn">{{ noSourceNote }}</p>
      <p v-if="!state.enabled" class="heat-note">
        {{
          tt(
            'The switch is off, so the contact cannot rise.',
            'Stikalo je izklopljeno, zato se kontakt ne more vklopiti.',
          )
        }}
      </p>
      <p v-if="guardNote" class="heat-note">{{ guardNote }}</p>

      <SetpointTimeline
        :device-values="device.deviceValues ?? []"
        :device-id="device.id"
        accent-color="rgb(249, 115, 22)" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import SetpointTimeline from './SetpointTimeline.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { useEntitiesStore } from '@/stores/entities';
import { tt } from '../../utils/consoleText';
import {
  heatDemandState,
  heatDemandThresholds,
  heatDemandZones,
  type HeatDemandSource,
  type HeatDemandZone,
} from '../../utils/heatDemandZones';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  colSpan?: number;
  rowSpan?: number;
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [deviceId: string, newValue: unknown];
}>();

/** The controller's own accent — the fire it decides about. */
const HEAT_ACCENT = 'rgb(249, 115, 22)';

const entitiesStore = useEntitiesStore();
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (rows) => {
    if (rows?.length) initializeValues(rows);
  },
  { deep: true },
);

/** The device's readings: the socket's live map over the row's last known ones. */
const values = computed<Record<string, unknown>>(() => {
  const raw: Record<string, unknown> = {};
  for (const row of props.device.deviceValues ?? []) {
    const key = String(row.valueKey ?? '');
    if (key) raw[key] = row.currentValue;
  }
  Object.assign(raw, currentValues.value);

  // A handler device's rows can be stored prefixed (`heat_demand_controller__
  // demand1`); the zone resolver reads the plain key the class declares.
  const plain: Record<string, unknown> = { ...raw };
  for (const [key, reading] of Object.entries(raw)) {
    const separator = key.indexOf('__');
    if (separator > 0) plain[key.slice(separator + 2)] = reading;
  }
  return plain;
});

const thresholds = computed(() => heatDemandThresholds(props.device.settings));

/**
 * valueId → the device and value behind it, so a zone can carry the name the
 * operator gave its thermostat. Built once per device-list change rather than
 * per zone row.
 */
const valueIndex = computed(() => {
  const index = new Map<string, HeatDemandSource>();
  for (const device of entitiesStore.devices) {
    const rows = entitiesStore.getDeviceValueRows(device) ?? [];
    if (rows.length === 0) continue;
    const deviceName = (device.shortName || device.deviceName || '').trim();
    for (const row of rows) {
      const id = String(row.id ?? '');
      if (!id) continue;
      index.set(id, { deviceName, valueName: row.name ?? null });
    }
  }
  return index;
});

function sourceOf(valueId: string): HeatDemandSource | null {
  return valueIndex.value.get(valueId) ?? null;
}

const zones = computed(() => heatDemandZones(props.device.settings, values.value, sourceOf));
const state = computed(() => heatDemandState(zones.value, values.value, thresholds.value));
const contact = computed(() => state.value.contact);

const highestDemand = computed(() => {
  const readings = zones.value.map((zone) => zone.demand).filter((value): value is number => value !== null);
  return readings.length > 0 ? Math.max(...readings) : null;
});

const stateWord = computed(() => {
  if (!state.value.enabled) return tt('Blocked', 'Preprečeno');
  return contact.value
    ? tt('Heat demand ON', 'Potreba po toploti VKLOP')
    : tt('Heat demand OFF', 'Potreba po toploti IZKLOP');
});

const caption = computed(() => {
  const total = zones.value.length;
  if (total === 0) return tt('No zone demand right now', 'Trenutno ni potreb po conah');
  const asking = state.value.askingCount;
  if (contact.value) {
    return tt(
      `${state.value.activeCount} of ${total} zones holding`,
      `${state.value.activeCount} od ${total} con drži`,
    );
  }
  return tt(`${asking} of ${total} zones asking`, `${asking} od ${total} con zahteva`);
});

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function formatPercent(value: number): string {
  return `${Math.round(value)} %`;
}

/** The handler's band, drawn where it acts. */
const bandStyle = computed(() => ({
  left: `${clampPercent(thresholds.value.releaseAt)}%`,
  width: `${Math.max(0, clampPercent(thresholds.value.threshold) - clampPercent(thresholds.value.releaseAt))}%`,
}));

const markStyle = computed(() => ({ left: `${clampPercent(thresholds.value.threshold)}%` }));

/** A zone inside the band is what a held-on contact is holding. */
function isHeld(zone: HeatDemandZone): boolean {
  return contact.value && zone.demand !== null && !zone.windowOpen && zone.demand > thresholds.value.releaseAt;
}

function zoneWord(zone: HeatDemandZone): string {
  if (zone.windowOpen) return tt('window open', 'okno odprto');
  if (zone.demand === null) return tt('no reading', 'ni odčitka');
  if (zone.asking) return tt('asking', 'zahteva');
  if (isHeld(zone)) return tt('holding', 'drži');
  return tt('idle', 'miruje');
}

const noSourceNote = computed(() => {
  if (!state.value.noSource) return '';
  const timeout = thresholds.value.noSourceTimeoutSeconds;
  if (!contact.value) {
    return tt('No zone is reporting demand.', 'Nobena cona ne poroča potrebe.');
  }
  if (timeout <= 0) {
    return tt(
      'No zone is reporting demand — the watchdog is disabled, so the contact stays as it is.',
      'Nobena cona ne poroča potrebe — nadzor je izklopljen, zato kontakt ostane tak, kot je.',
    );
  }
  const minutes = Math.max(1, Math.round(timeout / 60));
  return tt(
    `No zone is reporting demand — the contact drops after ${minutes} min.`,
    `Nobena cona ne poroča potrebe — kontakt se izklopi po ${minutes} min.`,
  );
});

const guardNote = computed(() => {
  const on = Math.round(thresholds.value.minOnSeconds / 60);
  const off = Math.round(thresholds.value.minOffSeconds / 60);
  if (on <= 0 && off <= 0) return '';
  return tt(
    `Anti-short-cycle: at least ${on} min on and ${off} min off.`,
    `Zaščita pred kratkimi cikli: najmanj ${on} min vklop in ${off} min izklop.`,
  );
});

async function toggleEnabled(): Promise<void> {
  if (controlling.value) return;
  const ok = await setProperty(props.device, 'enabled', !state.value.enabled);
  if (ok) emit('value-update', props.device.id, !state.value.enabled);
}

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

// colSpan/rowSpan arrive like they do for the other layout-aware surfaces; the
// sheet itself is fluid.
void props.colSpan;
void props.rowSpan;
</script>

<style scoped>
.heat-sheet {
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  padding: 0.25em 0;
}

.heat-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35em;
}

.heat-circle {
  display: grid;
  place-items: center;
  width: 4.5em;
  height: 4.5em;
  border-radius: 9999px;
  border: 2px solid var(--device-color-heat, rgb(249, 115, 22));
  color: var(--device-color-heat, rgb(249, 115, 22));
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 12%, transparent);
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    opacity 0.15s ease;
}

.heat-circle--on {
  background: var(--device-color-heat, rgb(249, 115, 22));
  color: #fff;
}

.heat-circle--blocked {
  opacity: 0.45;
  border-style: dashed;
}

.heat-state {
  font-weight: 600;
  font-size: 0.95em;
  color: rgb(71, 85, 105);
}

.heat-state--on {
  color: var(--device-color-heat, rgb(249, 115, 22));
}

.heat-state--blocked {
  color: rgb(148, 163, 184);
}

.heat-caption {
  font-size: 0.8em;
  color: rgb(100, 116, 139);
}

.heat-band {
  margin: 0;
  text-align: center;
  font-size: 0.75em;
  color: rgb(100, 116, 139);
}

.heat-zones {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.heat-zone {
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  padding: 0.45em 0.6em;
  border-radius: 0.6em;
  background: rgb(248, 250, 252);
  border: 1px solid transparent;
}

.heat-zone--asking {
  border-color: var(--device-color-heat, rgb(249, 115, 22));
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 8%, transparent);
}

.heat-zone--held {
  border-color: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 45%, transparent);
}

.heat-zone--window,
.heat-zone--silent {
  opacity: 0.65;
}

.heat-zone__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5em;
}

.heat-zone__name {
  font-size: 0.85em;
  font-weight: 600;
  color: rgb(30, 41, 59);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.heat-zone__name--placeholder {
  font-weight: 500;
  color: rgb(100, 116, 139);
}

.heat-zone__value {
  font-size: 0.85em;
  font-variant-numeric: tabular-nums;
  color: rgb(51, 65, 85);
}

.heat-rail {
  position: relative;
  height: 0.35em;
  border-radius: 9999px;
  background: rgb(226, 232, 240);
  overflow: hidden;
}

.heat-rail__band {
  position: absolute;
  top: 0;
  bottom: 0;
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 22%, transparent);
}

.heat-rail__fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  border-radius: 9999px;
  background: var(--device-color-heat, rgb(249, 115, 22));
}

.heat-zone--silent .heat-rail__fill,
.heat-zone--window .heat-rail__fill {
  background: rgb(148, 163, 184);
}

.heat-rail__mark {
  position: absolute;
  top: -1px;
  bottom: -1px;
  width: 1px;
  background: rgb(71, 85, 105);
  opacity: 0.55;
}

.heat-zone__meta {
  display: flex;
  gap: 0.4em;
  font-size: 0.72em;
  color: rgb(100, 116, 139);
}

.heat-zone__word {
  text-transform: lowercase;
}

.heat-zone__detail::before {
  content: '·';
  margin-right: 0.4em;
}

.heat-note {
  margin: 0;
  font-size: 0.75em;
  color: rgb(100, 116, 139);
}

.heat-note--warn {
  color: rgb(180, 83, 9);
}

.heat-empty {
  font-size: 0.8em;
  color: rgb(100, 116, 139);
  text-align: center;
}

.dark .heat-zone {
  background: rgb(30, 41, 59);
}

.dark .heat-zone__name,
.dark .heat-zone__value {
  color: rgb(226, 232, 240);
}
</style>
